import { useEffect, useRef, useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../shared/Button';
export function VerifiedEmailHelp({ onVerified }: { onVerified: () => void }) {
  const { currentUser } = useAuth();
  const { t } = useTranslation('dashboard');
  const verifiedCallback = useRef(onVerified);
  verifiedCallback.current = onVerified;
  useEffect(() => {
    let cancelled = false;
    // Persisted Firebase users can retain an old emailVerified value.
    // Trust a fresh server record and token, never the provider label alone.
    const refresh = async () => {
      if (!currentUser) return;
      try {
        await currentUser.reload();
        if (cancelled || !currentUser.emailVerified) return;
        await currentUser.getIdToken(true);
        if (!cancelled) verifiedCallback.current();
      } catch {
        // Keep the existing manual verification/retry controls available.
      }
    };
    void refresh();
    return () => { cancelled = true; };
  }, [currentUser]);
  const [sent, setSent] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const run = async (check: boolean) => {
    if (!currentUser || busy) return;
    setBusy(true);
    setError('');
    try {
      if (check) {
        await currentUser.reload();
        await currentUser.getIdToken(true);
        if (currentUser.emailVerified) onVerified();
        else setError(t('team.verifyEmail'));
      } else {
        await sendEmailVerification(currentUser);
        setSent(true);
      }
    } catch {
      setError(t('team.actionError'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <p>{t('team.verifyEmail')}</p>
      <p className="text-sm break-all">{currentUser?.email}</p>
      {error && <p role="alert">{error}</p>}
      {sent && <p role="status">{t('team.verificationSent')}</p>}
      <div className="flex flex-wrap gap-2">
        <Button disabled={sent || busy || !currentUser?.email} onClick={() => run(false)}>
          {t('team.sendVerification')}
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => run(true)}>
          {t('team.checkVerification')}
        </Button>
      </div>
    </div>
  );
}
